import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { ListFilter, Upload, UserRound } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CourseStage } from './model';
import SearchInput from '~/components/Inputs/Search';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import CourseStageFilter from './CourseStageFilter';

type PatientListToolbarProps = {
    /** Patients the current query matches, once known; undefined while loading or failed. */
    totalCount: number | undefined;
    /** Whether the search term or a filter narrowed the query the total counts against. */
    isNarrowed: boolean;
    searchInput: string;
    onSearchInputChange: (value: string) => void;
    /** Tints the filter control, so an active filter is visible from the toolbar too. */
    filtersActive: boolean;
    /** Opens the filter menu against the control the user pressed. */
    onOpenFilterMenu: (anchor: HTMLElement) => void;
    /** True while the filter menu opened from this control is open. */
    filterMenuExpanded: boolean;
    /** Id of the filter menu, for aria-controls while expanded. */
    filterMenuId: string;
    /** The screen focuses this control after a chip removes itself. */
    filterButtonRef: Ref<HTMLButtonElement>;
    courseStage: CourseStage | undefined;
    onCourseStageChange: (stage: CourseStage | undefined) => void;
    /** The "assigned to me" toggle; undefined where patients cannot be assigned, so it is not offered. */
    assignedToMe: AssignedToMeToggleProps | undefined;
    /** True while the list is re-fetched for a changed query; the count shown is then the previous one. */
    isFetching: boolean;
    onImport: () => void;
    /** Rendered after the stage strip: the active-filter chips. */
    activeFilters?: ReactNode;
};

/**
 * The block above the list: the title with the match count on one line,
 * then one row of controls, patient search, the filter-menu control, the
 * "assigned to me" toggle, the course-stage strip, whatever is active in
 * the filters, and the import action at the end. Kept to two short lines so
 * the list gets the height; the control row wraps only when the window
 * cannot hold it. Sorting lives in the column headers.
 *
 * The refresh indicator sits at the end of the title line, in the place of
 * the count it is replacing, so pressing a filter never shifts the controls
 * the user is pressing.
 */
export default function PatientListToolbar({
    totalCount,
    isNarrowed,
    searchInput,
    onSearchInputChange,
    filtersActive,
    onOpenFilterMenu,
    filterMenuExpanded,
    filterMenuId,
    filterButtonRef,
    courseStage,
    onCourseStageChange,
    assignedToMe,
    isFetching,
    onImport,
    activeFilters,
}: PatientListToolbarProps) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                px: 3,
                pt: 1.5,
                pb: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography variant="h6" component="h1">
                    <FormattedMessage id="patientList.title" />
                </Typography>
                {/* The previous count stays, dimmed, while the query
                    re-runs: a count that vanished and came back would
                    flicker, and the spinner beside it says why it is stale. */}
                <Typography
                    variant="body2"
                    sx={{
                        opacity: isFetching ? 0.5 : 1,
                        transition: 'opacity 150ms',
                    }}
                >
                    {totalCount !== undefined && (
                        <FormattedMessage
                            id={
                                isNarrowed
                                    ? 'patientList.subtitle.matchCount'
                                    : 'patientList.subtitle.count'
                            }
                            values={{ totalCount }}
                        />
                    )}
                </Typography>
                {isFetching && (
                    <CircularProgress size={14} sx={{ alignSelf: 'center' }} />
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                <SearchInput
                    value={searchInput}
                    onChange={onSearchInputChange}
                    placeholder={intl.formatMessage({
                        id: 'patientList.search.placeholder',
                    })}
                    clearLabel={intl.formatMessage({
                        id: 'patientList.search.clear',
                    })}
                    inputProps={{
                        'aria-label': intl.formatMessage({
                            id: 'patientList.search.ariaLabel',
                        }),
                    }}
                    sx={{ width: 300 }}
                />
                <IconButton
                    ref={filterButtonRef}
                    size="small"
                    selected={filtersActive}
                    onClick={(event) => onOpenFilterMenu(event.currentTarget)}
                    aria-haspopup="true"
                    aria-expanded={filterMenuExpanded}
                    aria-controls={
                        filterMenuExpanded ? filterMenuId : undefined
                    }
                    aria-label={intl.formatMessage({
                        id: 'patientList.filter.open',
                    })}
                    sx={{
                        color: filtersActive
                            ? 'primary.main'
                            : 'text.secondary',
                    }}
                >
                    <ListFilter size={16} />
                </IconButton>
                {assignedToMe && <AssignedToMeToggle {...assignedToMe} />}
                <CourseStageFilter
                    selected={courseStage}
                    onChange={onCourseStageChange}
                />
                {activeFilters}
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="contained"
                    startIcon={<Upload size={14} />}
                    onClick={onImport}
                >
                    <FormattedMessage id="patientList.toolbar.import" />
                </Button>
            </Box>
        </Box>
    );
}

/** The one value of the "assigned to me" toggle group. */
const ASSIGNED_TO_ME = 'assignedToMe';

export type AssignedToMeToggleProps = {
    /** Whether the list is narrowed to the signed-in user's patients. */
    pressed: boolean;
    /**
     * Narrows to (or widens from) the signed-in user's patients; undefined
     * while nobody is signed in, when the control is offered but disabled.
     */
    onChange: ((pressed: boolean) => void) | undefined;
};

/**
 * The "assigned to me" toggle. Disabled, with the reason reachable from the
 * keyboard, while nobody is signed in: there is then no "me" to match.
 */
function AssignedToMeToggle({ pressed, onChange }: AssignedToMeToggleProps) {
    const intl = useIntl();
    return (
        <DisabledHint
            title={
                onChange === undefined
                    ? intl.formatMessage({
                          id: 'patientList.assignedToMe.signedOut',
                      })
                    : undefined
            }
        >
            {/* A group of one, so the toggle is boxed and sized exactly
                like the stage strip beside it. */}
            <ToggleButtonGroup
                size="small"
                value={pressed ? [ASSIGNED_TO_ME] : []}
                disabled={onChange === undefined}
                onChange={() => onChange?.(!pressed)}
            >
                <ToggleButton value={ASSIGNED_TO_ME} sx={{ gap: 0.75 }}>
                    <UserRound size={14} aria-hidden />
                    <FormattedMessage id="patientList.assignedToMe" />
                </ToggleButton>
            </ToggleButtonGroup>
        </DisabledHint>
    );
}
